import { IReactionDisposer, autorun } from 'mobx';
import { Observable, OperatorFunction } from 'rxjs';

export interface MapMobxConfig {
    /**
     * If true, the MobX reactive view will be disposed when the source Observable completes.
     *
     * If false, the MobX reactive view will continue to run even after the source Observable completes
     * (similar behavior to `switchMap`).
     *
     * Defaults to `false`.
     */
    disposeOnComplete?: boolean;
}

/**
 * Projects each source value to an Observable which emits the initial value immediately,
 * and then emits values when the MobX value changes.
 *
 * To directly convert from MobX to RxJS, see our `fromMobx()` function.
 *
 * @param project A function that, when applied to an item emitted by the source Observable, returns a MobX reactive view.
 * @example
 * // Our input Observable.
 * const source$ = new BehaviorSubject('rxjs ');
 * // A MobX reactive value.
 * const mobxObs = observable.box('mobx');
 * // This will print 'rxjs mobx' immediately.
 * source$.pipe(mapMobx(x => {
 *   return x + mobxObs.get();
 * })).subscribe(x => console.log(x));
 * // Then this will print 'rxjs mobx2'.
 * mobxObs.set('mobx2');
 */
export function mapMobx<T, S>(project: (value: T) => S, config?: MapMobxConfig): OperatorFunction<T, S> {
    return (source: Observable<T>) => {
        return new Observable<S>(destination => {
            let disposeAutorun: IReactionDisposer | undefined;
            const sub = source.subscribe({
                next(value: T) {
                    disposeAutorun?.();
                    disposeAutorun = autorun(() => {
                        destination.next(project(value));
                    }, {
                        onError(e: unknown) {
                            destination.error(e);
                        },
                    });
                },
                error(error: unknown) {
                    destination.error(error);
                },
                complete() {
                    if (config?.disposeOnComplete) {
                        destination.complete();
                    }
                },
            });
            return () => {
                disposeAutorun?.();
                sub.unsubscribe();
            };
        });
    };
}
